import React, { useState, Fragment } from 'react';
import { EditIcon, ArchiveIcon, RefreshCwIcon, EyeIcon, TrashIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetCard } from './BudgetCard';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetListProps {
  budgets: Budget[];
  onEdit: (budget: Budget) => void;
  onArchive: (budgetId: string) => void;
  onRestore: (budgetId: string) => void;
  onDelete?: (budgetId: string) => void;
  locale: string;
  showOrgUnit?: boolean;
}
export function BudgetList({
  budgets,
  onEdit,
  onArchive,
  onRestore,
  onDelete,
  locale,
  showOrgUnit = false
}: BudgetListProps) {
  const [expandedBudgetId, setExpandedBudgetId] = useState<string | null>(null);
  const toggleExpandBudget = (budgetId: string) => {
    setExpandedBudgetId(expandedBudgetId === budgetId ? null : budgetId);
  };
  return <div data-id="element-7232">
      {budgets.length === 0 ? <div className="text-center py-12" data-id="element-7233">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-7234">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-7235" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-7236">
            No budgets found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-7237">
            Get started by creating a new budget.
          </p>
        </div> : <div className="overflow-hidden" data-id="element-7238">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-7239">
            <thead className="bg-gray-50" data-id="element-7240">
              <tr data-id="element-7241">
                {showOrgUnit && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7242">
                    Organisation Unit
                  </th>}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7243">
                  Budget Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7244">
                  Budget Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7245">
                  GL Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7246">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7247">
                  Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7248">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7249">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-7250">
              {budgets.map(budget => <Fragment key={budget.id} data-id="element-7251">
                  <tr className="hover:bg-gray-50" data-id="element-7252">
                    {showOrgUnit && <td className="px-6 py-4" data-id="element-7253">
                        <div className="text-sm font-medium text-gray-900" data-id="element-7254">
                          {budget.orgUnitName}
                        </div>
                        {budget.orgUnitPath && <div className="text-sm text-gray-500" data-id="element-7255">
                            {budget.orgUnitPath}
                          </div>}
                      </td>}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-7256">
                      {budget.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7257">
                      {budget.budgetTypeName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono" data-id="element-7258">
                      {budget.glCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7259">
                      <div className="font-medium" data-id="element-7260">
                        {formatCurrency(budget.remainingAmount, budget.currency, locale)}{' '}
                        <span className="text-xs text-gray-400" data-id="element-7261">remaining</span>
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-7262">
                        of{' '}
                        {formatCurrency(budget.totalAmount, budget.currency, locale)}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1" data-id="element-7263">
                        <div className={`h-1.5 rounded-full ${budget.remainingAmount / budget.totalAmount > 0.5 ? 'bg-green-500' : budget.remainingAmount / budget.totalAmount > 0.2 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                    width: `${Math.max(budget.remainingAmount / budget.totalAmount * 100, 0)}%`
                  }} data-id="element-7264"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7265">
                      {new Date(budget.startDate).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}{' '}
                      →{' '}
                      {new Date(budget.endDate).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-7266">
                      <BudgetStatusBadge status={budget.status} data-id="element-7267" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7268">
                      <div className="flex justify-end space-x-2" data-id="element-7269">
                        <button onClick={() => toggleExpandBudget(budget.id)} className="text-green-600 hover:text-green-900" title="View Details" data-id="element-7270">
                          <EyeIcon className="h-4 w-4" data-id="element-7271" />
                        </button>
                        <button onClick={() => onEdit(budget)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-7272">
                          <EditIcon className="h-4 w-4" data-id="element-7273" />
                        </button>
                        {budget.status === 'Active' ? <button onClick={() => onArchive(budget.id)} className="text-gray-600 hover:text-gray-900" title="Archive" data-id="element-7274">
                            <ArchiveIcon className="h-4 w-4" data-id="element-7275" />
                          </button> : <button onClick={() => onRestore(budget.id)} className="text-green-600 hover:text-green-900" title="Restore" data-id="element-7276">
                            <RefreshCwIcon className="h-4 w-4" data-id="element-7277" />
                          </button>}
                        {onDelete && <button onClick={() => onDelete(budget.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-7278">
                            <TrashIcon className="h-4 w-4" data-id="element-7279" />
                          </button>}
                      </div>
                    </td>
                  </tr>
                  {expandedBudgetId === budget.id && <tr data-id="element-7280">
                      <td colSpan={showOrgUnit ? 9 : 8} className="px-6 py-4 bg-gray-50" data-id="element-7281">
                        <BudgetCard budget={budget} locale={locale} data-id="element-7282" />
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}
    </div>;
}